'use es6';

import { pick } from 'laboratory-lib/core/helpers';

const parse = query => {
  if (query == null || query.trim() === '') {
    return {};
  }

  return query.split('&').reduce((result, current) => {
    const [key, value] = current.split('=');
    let keyName = key;
    const decodedValue = decodeURIComponent(value); // parse query strings in the form foo[] for arrays. This is only for compatibility and
    // repeating keys should be favored.

    if (keyName.length > 2 && keyName.lastIndexOf('[]') === keyName.length - 2) {
      keyName = keyName.substring(0, keyName.length - 2);
    }

    const existing = result[keyName];

    if (existing !== undefined) {
      if (Array.isArray(existing)) {
        existing.push(decodedValue);
      } else {
        result[keyName] = [existing, decodedValue];
      }
    } else {
      result[keyName] = decodedValue;
    }

    return result;
  }, {});
};

function ensureFn(fn, fallback = () => {}) {
  if (typeof fn !== 'function') {
    return fallback;
  }

  return fn;
}

function extractProperty(objs = [], property = '', defaultValue = '') {
  const match = objs.find(obj => obj && typeof obj[property] !== 'undefined');
  return match ? match[property] : defaultValue;
}

export function isExpired(updatedAt, maxAge) {
  const timeInStoreMs = Math.abs(Date.now() - updatedAt);
  return timeInStoreMs > maxAge;
}
export function getCacheKey(treatmentKey, paramKey) {
  return `${treatmentKey}-${paramKey}`;
}
export function getCacheKeys({
  schema
}) {
  return Object.keys(schema).reduce((acc, treatmentKey) => {
    const {
      parameters,
      maxAge = undefined
    } = schema[treatmentKey];
    Object.keys(parameters).forEach(paramKey => {
      acc.push({
        key: getCacheKey(treatmentKey, paramKey),
        maxAge
      });
    });
    return acc;
  }, []);
}
export function getOverridesFromSearch({
  cacheKeys,
  search = ''
}) {
  const overrideKeys = cacheKeys.map(({
    key
  }) => key); // remove ? from start of search query

  const cleaned = search && search.length && search[0] === '?' ? search.substr(1, search.length) : '';
  return pick(parse(cleaned), overrideKeys);
}
export function getOverridesFromCache({
  cacheKeys,
  asyncStore,
  onError
}) {
  const asyncStoreValues = [];
  const overrides = {};

  if (typeof asyncStore === 'function') {
    cacheKeys.forEach((cacheKey = {}) => {
      const asyncValue = asyncStore('get', cacheKey.key).then((cacheEntry = {}) => {
        const {
          updatedAt = 0,
          value = ''
        } = cacheEntry || {};

        if (value && updatedAt) {
          const key = extractProperty([cacheKey, cacheEntry], 'key', '');
          const maxAge = extractProperty([cacheKey, cacheEntry], 'maxAge', 0);

          if (!isExpired(updatedAt, maxAge)) {
            overrides[key] = value;
          }
        }
      }).catch(ensureFn(onError));
      asyncStoreValues.push(asyncValue);
    });
  }

  return Promise.all(asyncStoreValues).then(() => overrides);
}
export function saveOverridesToCache({
  schema,
  treatments,
  asyncStore,
  onError
}) {
  const asyncStoreValues = [];

  if (typeof asyncStore === 'function') {
    Object.keys(treatments).forEach(treatmentKey => {
      const treatment = treatments[treatmentKey];
      Object.keys(treatment.parameters).forEach(paramKey => {
        const cacheKey = getCacheKey(treatmentKey, paramKey);
        asyncStore('get', cacheKey).then((cacheEntry = {}) => {
          const {
            updatedAt = 0
          } = cacheEntry || {};
          const maxAge = extractProperty([schema[treatmentKey], treatment, cacheEntry], 'maxAge', 0);
          const value = treatment['parameters'][paramKey];

          if (maxAge > 0 && isExpired(updatedAt, maxAge)) {
            asyncStoreValues.push(asyncStore('set', cacheKey, {
              key: cacheKey,
              updatedAt: Date.now(),
              maxAge,
              value
            }).catch(ensureFn(onError)));
          } else if (!!updatedAt && maxAge === 0) {
            asyncStoreValues.push(asyncStore('delete', cacheKey).catch(ensureFn(onError)));
          }
        }).catch(ensureFn(onError));
      });
    });
  }

  return Promise.all(asyncStoreValues);
}